import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API_URL } from '../utils/api';

/**
 * Coach: Create and manage custom quizzes for apprentices.
 * Route: /coach/quizze
 */
const QuizBuilder = () => {
    const { fetchWithAuth } = useContext(AuthContext);
    const navigate = useNavigate();

    const [quizzes, setQuizzes] = useState([]);
    const [groups, setGroups] = useState([]);
    const [plants, setPlants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    // New quiz form state
    const [title, setTitle] = useState('');
    const [quizMode, setQuizMode] = useState('FLASHCARD');
    const [dueDate, setDueDate] = useState('');
    const [targetGroup, setTargetGroup] = useState('');
    const [selectedPlants, setSelectedPlants] = useState([]);
    const [plantSearch, setPlantSearch] = useState('');
    const [showResults, setShowResults] = useState(null);
    const [results, setResults] = useState([]);

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        setLoading(true);
        try {
            const [qRes, gRes, pRes] = await Promise.all([
                fetchWithAuth('/api/coach/quizzes/'),
                fetchWithAuth('/api/coach/groups/'),
                fetchWithAuth('/api/plants/'),
            ]);
            const qData = await qRes.json();
            const gData = await gRes.json();
            const pData = await pRes.json();
            setQuizzes(Array.isArray(qData) ? qData : qData.results || []);
            setGroups(Array.isArray(gData) ? gData : gData.results || []);
            setPlants(Array.isArray(pData) ? pData : pData.results || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const createQuiz = async () => {
        if (!title.trim() || selectedPlants.length === 0) return;
        const body = {
            title,
            quiz_mode: quizMode,
            due_date: dueDate || null,
            target_group: targetGroup || null,
            plant_ids: selectedPlants.map(p => p.id),
        };
        const res = await fetchWithAuth('/api/coach/quizzes/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (res.ok) {
            setShowCreate(false);
            setTitle(''); setSelectedPlants([]); setDueDate(''); setTargetGroup('');
            loadAll();
        }
    };

    const loadResults = async (quizId) => {
        setShowResults(quizId);
        const res = await fetchWithAuth(`/api/coach/quizzes/${quizId}/results/`);
        if (res.ok) setResults(await res.json());
    };

    const togglePlant = (plant) => {
        setSelectedPlants(prev =>
            prev.find(p => p.id === plant.id)
                ? prev.filter(p => p.id !== plant.id)
                : [...prev, plant]
        );
    };

    const filteredPlants = plants.filter(p =>
        (p.botanical_name?.toLowerCase().includes(plantSearch.toLowerCase()) ||
         p.german_name?.toLowerCase().includes(plantSearch.toLowerCase()))
    ).slice(0, 30);

    const modeLabels = { FLASHCARD: '🃏 Lernkarten', MULTIPLE_CHOICE: '✅ Multiple Choice', BLITZ: '⚡ Blitz-Quiz' };

    if (loading) return (
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="w-8 h-8 border-4 border-plantlix-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
            <button onClick={() => navigate('/coach')} className="text-sm text-gray-500 dark:text-gray-400 hover:text-plantlix-500 transition flex items-center gap-1">
                ← Dashboard
            </button>

            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">🧠 Quiz-Builder</h1>
                <button
                    onClick={() => setShowCreate(true)}
                    className="px-4 py-2 rounded-xl bg-purple-500 hover:bg-purple-600 text-white font-bold text-sm transition shadow-lg shadow-purple-500/20"
                >
                    + Neues Quiz
                </button>
            </div>

            {/* Existing quizzes */}
            {quizzes.length === 0 && !showCreate && (
                <div className="text-center py-16 text-gray-400">
                    <p className="text-5xl mb-4">🧪</p>
                    <p className="font-semibold text-lg text-gray-700 dark:text-gray-300">Noch keine Quizze erstellt</p>
                    <p className="text-sm mt-1">Erstelle ein Quiz und weise es deinen Azubis zu.</p>
                </div>
            )}

            {quizzes.length > 0 && (
                <div className="space-y-3">
                    {quizzes.map(q => (
                        <div key={q.id} className="bg-white dark:bg-[#1a1a1a] border border-gray-100 dark:border-white/5 rounded-2xl p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-bold text-gray-900 dark:text-white">{q.title}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {modeLabels[q.quiz_mode]} · {q.plant_count} Pflanzen · {q.target_label}
                                        {q.due_date && ` · Frist: ${q.due_date}`}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-gray-400">{q.result_count} Ergebnisse</span>
                                    <button
                                        onClick={() => loadResults(q.id)}
                                        className="px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-xs font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition"
                                    >
                                        📊 Ergebnisse
                                    </button>
                                </div>
                            </div>

                            {/* Results inline */}
                            {showResults === q.id && (
                                <div className="mt-4 border-t border-gray-100 dark:border-white/5 pt-4 space-y-2">
                                    {results.length === 0 && <p className="text-xs text-gray-400 text-center py-3">Noch keine Ergebnisse.</p>}
                                    {results.map(r => (
                                        <div key={r.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5">
                                            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{r.apprentice_name}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-xs text-gray-500">{r.cards_correct}/{r.cards_total}</span>
                                                <span className={`px-2 py-0.5 rounded-lg text-xs font-bold ${r.score_pct >= 80 ? 'bg-plantlix-50 text-plantlix-600' : r.score_pct >= 50 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>
                                                    {Math.round(r.score_pct)}%
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    <button onClick={() => setShowResults(null)} className="text-xs text-gray-400 hover:text-gray-600 mt-1">Schließen</button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Create modal */}
            {showCreate && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowCreate(false)} />
                    <div className="relative bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100 dark:border-white/5 p-6 space-y-5">
                        <h2 className="font-bold text-lg text-gray-900 dark:text-white">Neues Quiz erstellen</h2>

                        {/* Title */}
                        <input
                            value={title} onChange={e => setTitle(e.target.value)}
                            placeholder="Quiz-Titel (z.B. 'Gehölze Woche 12')"
                            className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-400"
                        />

                        {/* Mode */}
                        <div>
                            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-2">Quiz-Modus</label>
                            <div className="flex gap-2">
                                {Object.entries(modeLabels).map(([key, label]) => (
                                    <button key={key} onClick={() => setQuizMode(key)}
                                        className={`px-3 py-2 rounded-xl text-xs font-semibold border transition ${
                                            quizMode === key
                                                ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-400'
                                                : 'border-gray-200 dark:border-white/10 text-gray-500 hover:bg-gray-50 dark:hover:bg-white/5'
                                        }`}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Target group */}
                        <div>
                            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-2">Zielgruppe</label>
                            <select
                                value={targetGroup} onChange={e => setTargetGroup(e.target.value)}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-white"
                            >
                                <option value="">Alle Gruppen</option>
                                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                            </select>
                        </div>

                        {/* Due date */}
                        <div>
                            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-2">Frist (optional)</label>
                            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                                className="px-4 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-white"
                            />
                        </div>

                        {/* Plant picker */}
                        <div>
                            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 block mb-2">
                                Pflanzen ({selectedPlants.length} ausgewählt)
                            </label>
                            <input
                                value={plantSearch} onChange={e => setPlantSearch(e.target.value)}
                                placeholder="Pflanze suchen…"
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 text-sm text-gray-900 dark:text-white mb-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
                            />
                            {/* Selected chips */}
                            {selectedPlants.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                    {selectedPlants.map(p => (
                                        <span key={p.id} onClick={() => togglePlant(p)}
                                            className="px-2 py-1 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs font-semibold cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/50 transition">
                                            {p.botanical_name} ✕
                                        </span>
                                    ))}
                                </div>
                            )}
                            <div className="max-h-36 overflow-y-auto space-y-1 border border-gray-100 dark:border-white/5 rounded-xl p-2">
                                {filteredPlants.map(p => {
                                    const isSelected = selectedPlants.find(s => s.id === p.id);
                                    return (
                                        <button key={p.id} onClick={() => togglePlant(p)}
                                            className={`w-full text-left px-3 py-1.5 rounded-lg text-xs transition ${
                                                isSelected
                                                    ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 font-semibold'
                                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
                                            }`}>
                                            {p.botanical_name} {p.german_name && <span className="text-gray-400 ml-1">({p.german_name})</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                            <button onClick={createQuiz}
                                disabled={!title.trim() || selectedPlants.length === 0}
                                className="flex-1 py-3 rounded-xl bg-purple-500 hover:bg-purple-600 disabled:opacity-40 text-white font-bold text-sm transition">
                                Quiz erstellen
                            </button>
                            <button onClick={() => setShowCreate(false)}
                                className="px-5 py-3 rounded-xl border border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition">
                                Abbrechen
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default QuizBuilder;
